import { useEffect, useRef, useState } from "react";
import { api, formatTRY, formatNum, toNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileUp, Loader2, Plus, Trash2, Pencil, ChevronDown, ChevronUp, Leaf, BellRing, Send } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EarningsChart = ({ statements }) => {
  const rows = [...statements].sort((a, b) => a.period.localeCompare(b.period)).slice(-12)
    .map((s) => ({ label: `${MONTHS[+s.period.slice(4) - 1]?.slice(0, 3)} ${s.period.slice(2, 4)}`, earning: s.earning, vat: s.vat || 0, tax: Math.abs(s.tax || 0) }));
  if (rows.length < 1) return null;
  const style = { background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" };
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="hb-chart">
      <p className="font-heading text-base font-semibold">Aylık Net Kazanç (son {rows.length} ay)</p>
      <div className="mt-3 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" stroke="var(--muted-foreground)" fontSize={12} />
            <YAxis stroke="var(--muted-foreground)" fontSize={12} tickFormatter={(v) => formatNum(v, 0)} />
            <Tooltip contentStyle={style} formatter={(v) => formatTRY(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="earning" name="Net Kazanç" fill="var(--chart-1)" radius={[6, 6, 0, 0]} />
            <Bar dataKey="vat" name="KDV" fill="#d97706" radius={[6, 6, 0, 0]} />
            <Bar dataKey="tax" name="Vergi" fill="var(--destructive)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const KdvReminder = ({ isAdmin }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/myherbalife/kdv-reminder").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return null;
  const send = async () => {
    setBusy(true);
    try { const r = await api.post("/myherbalife/kdv-reminder/send"); toast.success(`Hatırlatma gönderildi: ${r.data.to.join(", ")}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="hb-kdv-reminder">
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-5 w-5 text-amber-600" />
        <div className="text-sm">
          <p><b>KDV beyan hatırlatması</b> — her ayın 24'ünde e-posta ile gönderilir; son gün <b>{d.deadline.split("-").reverse().join(".")}</b>.</p>
          <p className="text-muted-foreground">{d.invoice_month} ödemeleri için devlete ödenecek KDV: <b className="text-amber-600" data-testid="hb-kdv-due">{formatTRY(d.total_vat)}</b>{d.rows.length ? ` (${d.rows.length} ekstre)` : " — KDV'li ekstre yok"}</p>
          {d.last_run && <p className="text-xs text-muted-foreground">Son gönderim: {new Date(d.last_run.at).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })} · {d.last_run.sent ? `gönderildi (${d.last_run.to.join(", ")})` : d.last_run.reason || "gönderilmedi"}</p>}
        </div>
      </div>
      {isAdmin && <Button size="sm" variant="secondary" disabled={busy || !d.rows.length} onClick={send} data-testid="hb-kdv-send"><Send className="mr-2 h-3.5 w-3.5" /> Şimdi Gönder</Button>}
    </div>
  );
};

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const FIELDS = [["sponsor_bonus", "Sponsor Primi"], ["sales_bonus", "Satış Primi"], ["other", "Diğer İşlemler"], ["adjustments", "Önceki Ay Düzeltmeleri"], ["gross", "Brüt Kazanç"], ["tax", "Vergi Kesintisi (−)"], ["vat", "KDV (fatura)"], ["net", "Ödenen Tutar (Net)"], ["personal_vp", "Kişisel Satış Puanı"], ["group_vp", "Grup Satış Puanı"], ["total_vp", "Toplam Satış Puanı"]];
const EMPTY = { period: "", member_id: "", member_name: "", sponsor_bonus: "", sales_bonus: "", other: "", adjustments: "", gross: "", tax: "", vat: "", net: "", personal_vp: "", group_vp: "", total_vp: "", payment_date: "", payment_method: "", payment_no: "", note: "", taxpayer: false, orders: [] };

const Stat = ({ label, value, sub, tone = "" }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={`hb-stat-${label}`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl font-extrabold ${tone}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const StatementCard = ({ s, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const bonusOrders = s.orders.filter((o) => o.bonus > 0);
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={`hb-statement-${s.period}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <p className="font-heading text-lg font-extrabold">{s.label} <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{s.source === "pdf" ? "PDF" : "Manuel"}</span>{s.taxpayer && <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-600" data-testid={`hb-taxpayer-${s.period}`}>Vergi mükellefi</span>}</p>
          <p className="text-xs text-muted-foreground">{s.member_name && `${s.member_name} · `}{s.payment_date && `Ödeme ${s.payment_date.split("-").reverse().join(".")} · `}{s.payment_method}{s.note && ` · ${s.note}`}</p>
        </div>
        <div className="grid grid-cols-2 gap-4 text-right sm:grid-cols-4">
          <div><p className="text-[10px] uppercase text-muted-foreground">Brüt</p><p className="font-semibold">{formatTRY(s.gross)}</p></div>
          <div><p className="text-[10px] uppercase text-muted-foreground">Vergi</p><p className="font-semibold text-destructive">{formatTRY(s.tax)}</p></div>
          <div><p className="text-[10px] uppercase text-muted-foreground">KDV{s.taxpayer ? " / Ödenen" : ""}</p><p className="font-semibold text-amber-600">{formatTRY(s.vat || 0)}</p>{s.taxpayer && <p className="text-[10px] text-muted-foreground">{formatTRY(s.net)}</p>}</div>
          <div><p className="text-[10px] uppercase text-muted-foreground">Net Kazanç{s.taxpayer ? " (KDV hariç)" : ""}</p><p className="font-heading text-xl font-extrabold text-primary" data-testid={`hb-net-${s.period}`}>{formatTRY(s.earning)}</p></div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-8" onClick={() => onEdit(s)} data-testid={`hb-edit-${s.period}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => onDelete(s)} data-testid={`hb-delete-${s.period}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="secondary" className="h-8" onClick={() => setOpen(!open)} data-testid={`hb-toggle-${s.period}`}>{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border p-4 text-sm" data-testid={`hb-detail-${s.period}`}>
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {FIELDS.map(([k, l]) => <div key={k} className="rounded-lg bg-muted/40 px-3 py-2"><p className="text-[10px] uppercase text-muted-foreground">{l}</p><p className="font-semibold">{k.endsWith("_vp") ? formatNum(s[k], 2) : formatTRY(s[k])}</p></div>)}
          </div>
          {s.orders.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-primary">Siparişler ({s.orders.length}) · {bonusOrders.length} prim kazandıran</p>
              <table className="w-full min-w-[640px] text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Tarih</th><th>Sipariş</th><th>Satın Alan</th><th className="text-right">VP</th><th className="text-right">Tutar</th><th className="text-right">İsk. %</th><th className="text-right">Prim %</th><th className="text-right">Sponsor Primi</th></tr></thead>
                <tbody>
                  {s.orders.map((o, i) => (
                    <tr key={i} className={`border-t border-border ${o.bonus > 0 ? "" : "text-muted-foreground"}`} data-testid={`hb-order-${s.period}-${i}`}>
                      <td className="py-1">{o.date.split("-").reverse().join(".")}</td><td>{o.order_no}</td><td>{o.buyer}</td>
                      <td className="text-right">{formatNum(o.vp, 2)}</td><td className="text-right">{formatTRY(o.amount)}</td><td className="text-right">{formatNum(o.discount_pct, 0)}</td><td className="text-right">{formatNum(o.bonus_pct, 0)}</td>
                      <td className={`text-right font-semibold ${o.bonus > 0 ? "text-primary" : ""}`}>{formatTRY(o.bonus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function MyHerbalife() {
  const { user } = useAuth();
  const ref = useRef();
  const [data, setData] = useState({ statements: [], years: [], total: { gross: 0, tax: 0, net: 0, total_vp: 0, months: 0 } });
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [year, setYear] = useState("Tümü");

  const load = () => api.get("/myherbalife").then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Yüklenemedi"));
  useEffect(() => { load(); }, []);

  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/myherbalife/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setEdit({ ...EMPTY, ...r.data, id: null, fromPdf: true });
      if (r.data.exists) toast.info(`${r.data.label} ekstresi zaten var — kaydederseniz üzerine yazılır`);
    } catch (e) { toast.error(e.response?.data?.detail || "PDF okunamadı"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!/^\d{6}$/.test(edit.period)) return toast.error("Dönem YYYYAA biçiminde olmalı (örn. 202609)");
    const body = { ...edit };
    FIELDS.forEach(([k]) => { body[k] = toNum(edit[k]) || 0; });
    body.tax_base = toNum(edit.tax_base) || body.gross; body.vat_deduction = toNum(edit.vat_deduction) || 0; body.vat_addition = toNum(edit.vat_addition) || 0;
    body.taxpayer = !!edit.taxpayer;
    if (!body.gross) body.gross = body.sponsor_bonus + body.sales_bonus + body.other + body.adjustments;
    if (!body.net) body.net = body.gross + body.tax + (body.taxpayer ? body.vat : 0);
    delete body.id; delete body.fromPdf; delete body.exists; delete body.label; delete body.source; delete body.created_at; delete body.updated_at;
    setBusy(true);
    try {
      if (edit.id) await api.put(`/myherbalife/${edit.id}`, body); else await api.post("/myherbalife", body);
      toast.success("Ekstre kaydedildi"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  const del = async (s) => {
    if (!window.confirm(`${s.label} ekstresi silinsin mi?`)) return;
    try { await api.delete(`/myherbalife/${s.id}`); toast.success("Silindi"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const shown = year === "Tümü" ? data.statements : data.statements.filter((s) => s.period.startsWith(year));
  const sum = year === "Tümü" ? data.total : data.years.find((y) => y.year === year) || data.total;
  const last = data.statements[0];
  const setF = (k, v) => setEdit({ ...edit, [k]: v });

  return (
    <div className="space-y-6" data-testid="myherbalife-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">MyHerbalife</h1>
          <p className="mt-1 text-sm text-muted-foreground">Herbalife'tan kazandığınız sponsor & satış primleri — aylık kazanç ekstrelerini (Toptan Satış Kar Ekstresi) PDF olarak yükleyin</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={pick} data-testid="hb-pdf-input" />
          <Button disabled={busy} onClick={() => ref.current?.click()} data-testid="hb-pdf-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}{busy ? "PDF okunuyor…" : "Ekstre PDF Yükle"}</Button>
          <Button variant="secondary" onClick={() => setEdit({ ...EMPTY, id: null })} data-testid="hb-manual-btn"><Plus className="mr-2 h-4 w-4" /> Manuel Ekle</Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Net Kazanç" value={formatTRY(sum.earning ?? sum.net)} sub={`${sum.months} ay · ${year === "Tümü" ? "tüm dönemler" : year}${sum.vat ? " · KDV hariç" : ""}`} tone="text-primary" />
        <Stat label="Brüt Kazanç" value={formatTRY(sum.gross)} sub={`Sponsor ${formatTRY(sum.sponsor_bonus || 0)} · Satış ${formatTRY(sum.sales_bonus || 0)}`} />
        <Stat label="Vergi Kesintisi" value={formatTRY(sum.tax)} sub="Gelir vergisi stopajı" tone="text-destructive" />
        <Stat label="KDV" value={formatTRY(sum.vat || 0)} sub="Vergi mükellefi faturası · devlete ödenecek" tone="text-amber-600" />
        <Stat label="Toplam Satış Puanı" value={formatNum(sum.total_vp, 2)} sub={last ? `Son ay (${last.label}): ${formatNum(last.total_vp, 2)} VP` : ""} />
      </div>

      {last && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="hb-last">
          <Leaf className="h-5 w-5 text-primary" />
          <p className="text-sm"><b>Son ekstre: {last.label}</b> — net <b className="text-primary">{formatTRY(last.earning)}</b>{last.taxpayer ? ` (KDV hariç; ödenen ${formatTRY(last.net)})` : ""}, brüt {formatTRY(last.gross)}, {last.orders.length} sipariş{last.payment_date ? `, ödeme ${last.payment_date.split("-").reverse().join(".")}` : ""}</p>
        </div>
      )}

      {data.statements.length > 0 && <EarningsChart statements={data.statements} />}
      <KdvReminder isAdmin={user?.role === "admin"} />

      {data.years.length > 1 && (
        <div className="flex flex-wrap gap-1.5" data-testid="hb-year-tabs">
          {["Tümü", ...data.years.map((y) => y.year)].map((y) => (
            <button key={y} type="button" onClick={() => setYear(y)} data-testid={`hb-year-${y}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${year === y ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>{y}</button>
          ))}
        </div>
      )}

      {shown.length === 0 && <p className="rounded-xl border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="hb-empty">Henüz ekstre yok. "Ekstre PDF Yükle" ile MyHerbalife'tan indirdiğiniz aylık kazanç ekstresini yükleyin.</p>}
      <div className="space-y-3">{shown.map((s) => <StatementCard key={s.id} s={s} onEdit={(x) => setEdit({ ...x })} onDelete={del} />)}</div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="hb-edit-dialog">
          <DialogHeader><DialogTitle>{edit?.fromPdf ? `PDF'den Okunan Ekstre${edit.label ? ` — ${edit.label}` : ""}` : edit?.id ? "Ekstreyi Düzenle" : "Manuel Ekstre Ekle"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <label>Dönem (YYYYAA)<Input className="mt-1" value={edit.period} onChange={(e) => setF("period", e.target.value)} placeholder="202609" disabled={!!edit.id} data-testid="hb-f-period" /></label>
              <label>Üye No<Input className="mt-1" value={edit.member_id} onChange={(e) => setF("member_id", e.target.value)} data-testid="hb-f-member-id" /></label>
              <label>Üye Adı<Input className="mt-1" value={edit.member_name} onChange={(e) => setF("member_name", e.target.value)} data-testid="hb-f-member-name" /></label>
              <label className="col-span-2 flex items-center gap-2 rounded-lg border border-border px-3 py-2 sm:col-span-3">
                <input type="checkbox" checked={!!edit.taxpayer} onChange={(e) => setF("taxpayer", e.target.checked)} data-testid="hb-f-taxpayer" />
                <span>Vergi mükellefi — Herbalife stopaj kesmez, ödeme KDV'li fatura karşılığı yapılır. Net kazanç = ödenen tutar − KDV.</span>
              </label>
              {FIELDS.map(([k, l]) => <label key={k}>{l}<Input className="mt-1" inputMode="decimal" value={edit[k]} onChange={(e) => setF(k, e.target.value)} data-testid={`hb-f-${k}`} /></label>)}
              <label>Ödeme Tarihi<Input className="mt-1" type="date" value={edit.payment_date} onChange={(e) => setF("payment_date", e.target.value)} data-testid="hb-f-payment-date" /></label>
              <label>Ödeme Şekli<Input className="mt-1" value={edit.payment_method} onChange={(e) => setF("payment_method", e.target.value)} placeholder="EFT" data-testid="hb-f-payment-method" /></label>
              <label>Ödeme No<Input className="mt-1" value={edit.payment_no} onChange={(e) => setF("payment_no", e.target.value)} data-testid="hb-f-payment-no" /></label>
              <label className="col-span-2 sm:col-span-3">Not<Input className="mt-1" value={edit.note} onChange={(e) => setF("note", e.target.value)} data-testid="hb-f-note" /></label>
              {edit.orders?.length > 0 && <p className="col-span-2 text-xs text-muted-foreground sm:col-span-3">{edit.orders.length} sipariş satırı PDF'den okundu ve ekstreyle birlikte kaydedilecek.</p>}
              {edit.period && /^\d{6}$/.test(edit.period) && <p className="col-span-2 text-xs text-muted-foreground sm:col-span-3">Dönem: <b>{MONTHS[+edit.period.slice(4) - 1] || "?"} {edit.period.slice(0, 4)}</b> · Net kazanç: <b>{formatTRY((toNum(edit.net) || 0) - (edit.taxpayer ? toNum(edit.vat) || 0 : 0))}</b> · Brüt veya Ödenen boşsa otomatik hesaplanır.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Vazgeç</Button>
            <Button disabled={busy} onClick={save} data-testid="hb-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
